/**
 * Exercise List Page
 *
 * Lists all Quick Exercises. Uses CollectionView for zero/one/few/many states.
 */

import { useState, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Play,
  Calendar,
  Package,
  Zap,
  Archive,
  MoreVertical,
  Trash2,
  FileText,
} from 'lucide-react';
import { useDegapProcessStore } from '@/stores/prioritization/degapProcessStore';
import { useUserStore } from '@/stores/prioritization/userStore';
import { useUIStore } from '@/stores/prioritization/uiStore';
import { toFriendlyDbError } from '@/lib/friendlyDbError';
import { cycleDeleteConsequence } from '@/lib/workspaceSessionRules';
import { useTenantStore } from '@/stores/prioritization/tenantStore';
import { EntityListPage } from '../../../components/collection';
import type { FacetDefinition, SortOption } from '../../../components/collection/types';
import type { DegapCycle } from '../../../types/prioritization/degapCycle';

// ============================================================================
// Card renderer
// ============================================================================

function ExerciseCard({
  exercise,
  onOpen,
  onArchive,
  onDelete,
  canDelete,
}: {
  exercise: DegapCycle;
  onOpen: () => void;
  onArchive: () => void;
  onDelete: () => void;
  canDelete: boolean;
}) {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start gap-4">
        <div className="p-2.5 bg-[var(--mustard-dim)] flex-shrink-0">
          <Zap className="h-5 w-5 text-[var(--mustard)]" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <Link
              to={`/prioritization/exercises/${exercise.id}`}
              onClick={onOpen}
              className="text-base font-semibold text-[var(--text-pri)] hover:text-[var(--text-ter)] transition-colors"
            >
              {exercise.name}
            </Link>
            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
              exercise.status === 'active'    ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' :
              exercise.status === 'completed' ? 'bg-[var(--hover-tint)] text-[var(--text-sec)]'  :
              exercise.status === 'archived'  ? 'bg-[var(--hover-tint)] text-[var(--text-ter)]'  :
              /* planning */                    'bg-[var(--slate-dim)] text-[var(--text-sec)]'
            }`}>
              {exercise.status}
            </span>
          </div>

          {exercise.description && (
            <p className="text-sm text-[var(--text-ter)] mb-3 line-clamp-2">{exercise.description}</p>
          )}

          <div className="flex items-center gap-4 text-xs text-[var(--text-ter)]">
            <span className="flex items-center gap-1">
              <Package className="h-3.5 w-3.5" />
              {exercise.itemIds?.length ?? 0} items
            </span>
            <span className="flex items-center gap-1">
              <FileText className="h-3.5 w-3.5" />
              {exercise.outputs?.arrange?.snapshotIds?.length ?? 0} snapshots
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5" />
              {new Date(exercise.createdAt).toLocaleDateString()}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <Link
            to={`/prioritization/exercises/${exercise.id}`}
            onClick={onOpen}
            className="btn btn-primary btn-sm flex items-center gap-1 text-xs px-3 py-1.5"
          >
            <Play className="h-3.5 w-3.5" />
            Open
          </Link>

          <div className="relative">
            <button
              onClick={() => setMenuOpen(o => !o)}
              className="p-2 hover:bg-[var(--hover-tint)] transition-colors"
            >
              <MoreVertical className="h-4 w-4 text-[var(--text-ter)]" />
            </button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
                <div className="absolute right-0 top-full mt-1 w-40 bg-[var(--bg-raised)] shadow-lg border border-[var(--rule)] py-1 z-20">
                  {exercise.status !== 'archived' && (
                    <button
                      onClick={() => { onArchive(); setMenuOpen(false); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[var(--text-sec)] hover:bg-[var(--surface)]"
                    >
                      <Archive className="h-4 w-4" />
                      Archive
                    </button>
                  )}
                  {canDelete && (
                    <button
                      onClick={() => { onDelete(); setMenuOpen(false); }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[var(--coral)] hover:bg-[var(--coral-dim)]"
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Facets & sort
// ============================================================================

const FACETS: FacetDefinition<string>[] = [
  {
    field: 'status',
    label: 'Status',
    type: 'multi',
    options: [
      { value: 'active',    label: 'Active' },
      { value: 'planning',  label: 'Planning' },
      { value: 'completed', label: 'Completed' },
      { value: 'archived',  label: 'Archived' },
    ],
  },
];

const SORT_OPTIONS: SortOption<string>[] = [
  { field: 'name',      label: 'Name' },
  { field: 'status',    label: 'Status' },
  { field: 'createdAt', label: 'Created' },
];

// ============================================================================
// Page
// ============================================================================

export default function ExerciseList() {
  const navigate = useNavigate();
  const userId = useUserStore(state => state.userId);
  const can = useUserStore((s) => s.can);
  // can() reads the store, not React state: follow permissionsLoaded so this redraws when rights arrive.
  useUserStore((s) => s.permissionsLoaded);
  const canCreateExercise = can('process.create');
  const canDeleteExercise = can('process.edit');

  const cycles = useDegapProcessStore(state => state.cycles);
  const createQuickExercise = useDegapProcessStore(state => state.createQuickExercise);
  const setActiveCycle = useDegapProcessStore(state => state.setActiveCycle);
  const deleteCycle = useDegapProcessStore(state => state.deleteCycle);
  const archiveCycle = useDegapProcessStore(state => state.archiveCycle);

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [exerciseName, setExerciseName] = useState('');
  const [exerciseDescription, setExerciseDescription] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const exercises = useMemo(
    () => cycles.filter(c => c.uxMode === 'quick_exercise'),
    [cycles]
  );

  const addNotification = useUIStore((s) => s.addNotification);

  const handleCreateExercise = async () => {
    if (!exerciseName.trim()) return;
    setIsCreating(true);
    // createQuickExercise returns '' on failure; the reason only travels on the
    // store's `cycle-create-failed` event, so listen for it while we create.
    let failureReason: string | null = null;
    const onCreateFailed = (event: Event) => {
      failureReason = (event as CustomEvent<{ message?: string }>).detail?.message ?? null;
    };
    window.addEventListener('cycle-create-failed', onCreateFailed);
    try {
      const exerciseId = await createQuickExercise({
        name: exerciseName.trim(),
        description: exerciseDescription.trim() || undefined,
        createdBy: userId,
      });
      if (!exerciseId) {
        // Keep the form open with what was typed.
        addNotification({
          type: 'error',
          title: 'Exercise was not created',
          message: failureReason
            ? toFriendlyDbError(failureReason, 'ExerciseList.handleCreateExercise')
            : 'You may not have permission to create exercises in this workspace.',
          duration: 8000,
        });
        return;
      }
      setActiveCycle(exerciseId);
      setExerciseName('');
      setExerciseDescription('');
      setShowCreateForm(false);
      navigate(`/prioritization/exercises/${exerciseId}`);
    } catch (err) {
      console.error('Failed to create exercise:', err);
      addNotification({
        type: 'error',
        title: 'Exercise was not created',
        message: err instanceof Error ? err.message : 'Try again.',
        duration: 8000,
      });
    } finally {
      window.removeEventListener('cycle-create-failed', onCreateFailed);
      setIsCreating(false);
    }
  };

  const handleDelete = useCallback(async (id: string, name: string) => {
    // Said before the confirmation: what the workspace's rule does to the open sessions.
    const consequence = await cycleDeleteConsequence(id, useTenantStore.getState().getCurrentWorkspace?.()?.cycleDeletionPolicy);
    if (consequence.blocked) {
      addNotification({ type: 'error', title: 'Exercise was not deleted', message: consequence.line, duration: 9000 });
      return;
    }
    if (!confirm(`Delete "${name}"? ${consequence.line ? `${consequence.line} ` : ''}This cannot be undone.`)) return;
    const result = await deleteCycle(id);
    if (!result.success) {
      addNotification({
        type: 'error',
        title: 'Exercise was not deleted',
        message: `${toFriendlyDbError(result.error ?? 'The delete was refused.', 'ExerciseList.handleDelete')} You can archive it instead.`,
        duration: 9000,
      });
    }
  }, [deleteCycle, addNotification]);

  const renderCard = useCallback((exercise: DegapCycle) => (
    <ExerciseCard
      key={exercise.id}
      exercise={exercise}
      onOpen={() => setActiveCycle(exercise.id)}
      onArchive={() => archiveCycle(exercise.id)}
      onDelete={() => handleDelete(exercise.id, exercise.name)}
      canDelete={canDeleteExercise}
    />
  ), [setActiveCycle, archiveCycle, handleDelete, canDeleteExercise]);

  const renderItem = renderCard;

  return (
    <>
      <EntityListPage<DegapCycle, string>
        items={exercises}
        getId={(e) => e.id}
        masterDetail={false}
        title="Quick Exercises"
        subtitle={`${exercises.length} exercise${exercises.length !== 1 ? 's' : ''} · Simplified prioritization sessions`}
        icon={<Zap className="h-4 w-4 text-[var(--mustard)]" />}
        iconBgClassName="bg-[var(--mustard-dim)]"
        itemLabel="exercise"
        searchFields={['name', 'description']}
        searchPlaceholder="Search exercises…"
        sortOptions={SORT_OPTIONS}
        defaultSort={{ field: 'createdAt', direction: 'desc' }}
        facets={FACETS}
        viewModes={['card', 'list']}
        defaultViewMode="card"
        contentClassName="grid grid-cols-1 gap-4"
        renderItem={renderItem}
        renderCard={renderCard}
        onItemClick={(e) => {
          setActiveCycle(e.id);
          navigate(`/prioritization/exercises/${e.id}`);
        }}
        emptyTitle="No exercises yet"
        emptyDescription="Create a quick exercise to run a simplified prioritization session."
        emptyAction={
          canCreateExercise
            ? { label: 'Create Your First Exercise', onClick: () => setShowCreateForm(true) }
            : undefined
        }
        onCreate={canCreateExercise ? () => setShowCreateForm(true) : undefined}
        createLabel="New Exercise"
        shellClassName="h-[calc(100vh-6rem)]"
      />

      {/* Create Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[var(--bg-raised)] shadow-xl w-full max-w-lg p-6">
            <h2 className="text-xl font-semibold text-[var(--text-pri)] mb-4">Create Quick Exercise</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                  Exercise Name <span className="text-[var(--coral)]">*</span>
                </label>
                <input
                  type="text"
                  value={exerciseName}
                  onChange={e => setExerciseName(e.target.value)}
                  placeholder="e.g., Q1 Priority Review"
                  className="w-full px-4 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Description (optional)</label>
                <textarea
                  value={exerciseDescription}
                  onChange={e => setExerciseDescription(e.target.value)}
                  placeholder="What are you prioritizing?"
                  rows={3}
                  className="w-full px-4 py-2 border border-[var(--rule)] focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>
              <div className="p-3 bg-[var(--mustard-dim)] border border-[var(--mustard)] flex items-start gap-2">
                <Zap className="h-4 w-4 text-[var(--mustard)] mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-sm font-medium text-[var(--mustard)]">Quick Exercise Mode</div>
                  <div className="text-xs text-[var(--mustard)] mt-0.5">
                    Streamlined workflow: add items, set criteria, run tools, export results.
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => { setShowCreateForm(false); setExerciseName(''); setExerciseDescription(''); }}
                className="px-4 py-2 text-[var(--text-sec)] hover:bg-[var(--hover-tint)]"
                disabled={isCreating}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateExercise}
                disabled={!exerciseName.trim() || isCreating}
                className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:opacity-50"
              >
                {isCreating ? 'Creating…' : 'Create Exercise'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
